/**
 * Admin Dashboard page — the day-one landing view for tenant staff
 * (2026-07-09 wave, spec D1–D3; charter #221).
 *
 * Hierarchy (Principle 1 — one hero, supporting facts, everything else
 * behind a tap):
 *  1. Page-level data-trust banner (spec D2) — the server's data-status
 *     warning rendered VERBATIM when today's zeros are structural (the
 *     2026-07-09 incident class). One banner, above everything.
 *  2. FacilityHero — who's coming / here / leaving, staffing, alerts.
 *  3. First-time dogs, then ArrivalsDepartures — the actionable check-in
 *     list, right after the numbers that describe it (Sawyer, 10-30).
 *  4. DailyRollup.
 *  5. Role-gated analytics (RevenueChart / SegmentCards / InsightsPanel)
 *     — mounted ONLY for roles whose matrix rules allow the underlying
 *     endpoints (revenue:read — owner/manager). A receptionist fires
 *     ZERO fetches for widgets they can't see (spec D3).
 *
 * Moved off this page (spec D3, Johnny's locked #221 decision): the
 * tenant-wide ComplianceAlerts roster and VaxInsightCard now live on
 * /compliance; the hero's today-scoped alert strip replaces them here.
 *
 * Data flow: this page owns the /today and /data-status fetches and
 * passes them down, so the hero tiles and the arrivals list can never
 * disagree (Principle 1). The shell's Quick Book bumps
 * BookingRefreshContext; local check-ins bump localRefreshKey — both
 * fold into one refreshKey that refetches everything.
 */
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientConfig } from '@ferro/config';
import { readSession, todayIsoDate } from '../lib/api.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useBookingRefresh } from '../contexts/BookingRefreshContext.js';
import { rolesCanAccess, ROUTE_ACCESS_RULES } from '../lib/route-permissions.js';
import { effectiveRolesOf } from '../lib/rbac-matrix.js';
// Sawyer 02 kennel-stopgap (A1-stopgap)
import { Link } from 'react-router-dom';
import {
  canOpenMedsFeeding,
  MEDS_FEEDING_LABEL,
  medsFeedingHref,
} from '../lib/meds-feeding-link.js';
import { ArrivalsDepartures } from '../components/dashboard/ArrivalsDepartures.js';
import { AssessmentsToday } from '../components/dashboard/AssessmentsToday.js';
import {
  fetchAssessmentsToday,
  fetchDashboardDataStatus,
  fetchDashboardRollup,
  fetchDashboardToday,
  isScheduleDataSuspect,
} from '../components/dashboard/dashboard-api.js';
import { DailyRollup } from '../components/dashboard/DailyRollup.js';
import { FacilityHero } from '../components/dashboard/FacilityHero.js';
import { ArrivalsBoard } from '../components/arrivals-board/index.js';
import { Icon } from '../components/icons.js';
import { WelcomeCard } from '../components/WelcomeCard.js';
// Sawyer 15 workweek-hours-ot (E2)
import { DashboardOvertimeWatch } from '../components/timesheets/OvertimeWatchBanner.js';
import { InsightsPanel } from '../components/dashboard/InsightsPanel.js';
import { RevenueChart } from '../components/dashboard/RevenueChart.js';
import { SegmentCards } from '../components/dashboard/SegmentCards.js';
import { useDashboardResource } from '../components/dashboard/useDashboardResource.js';

type DashboardProps = {
  config: ClientConfig;
};

export function Dashboard({ config }: DashboardProps): JSX.Element {
  const { token, staff } = readSession();
  // BUG-6 — while the on-mount staff-token validation is in flight, hold
  // every dashboard fetch. A stale token would otherwise spray dead-token
  // requests → 401 burst → self-logout bounce. Once validated: valid →
  // everything fetches; invalid → AuthContext has cleared the session and
  // ProtectedRoute bounces, so this never re-renders.
  const { isValidatingSession } = useAuth();
  const { t } = useTranslation();
  const apiUrl = config.deployment.apiUrl;
  // D3 — analytics widgets are matrix-gated to the SAME rule the /reports
  // surface uses (revenue:read → owner/manager). Gating the MOUNT (not
  // just visibility) means a receptionist fires zero 403-able fetches.
  // #428 — union over primary + granted roles.
  const canSeeRevenue = rolesCanAccess(
    effectiveRolesOf(staff),
    ROUTE_ACCESS_RULES['/reports'],
  );
  const date = todayIsoDate();
  const [localRefreshKey, setLocalRefreshKey] = useState(0);
  // Spec C3 — the shell's Quick Book bumps this version on a successful
  // booking. Folding it into the widget refresh key means shell bookings
  // and local mutations (check-in) share one refresh mechanism.
  const { version: bookingRefreshVersion } = useBookingRefresh();
  const refreshKey = localRefreshKey + bookingRefreshVersion;

  const bumpRefresh = useCallback(() => {
    setLocalRefreshKey((k) => k + 1);
  }, []);

  // Sawyer, 10-30 — the hero's "Check in arriving dogs" scrolls to the list.
  const arrivalsRef = useRef<HTMLDivElement>(null);
  const jumpToArrivals = useCallback(() => {
    const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    arrivalsRef.current?.scrollIntoView?.({ behavior: calm ? 'auto' : 'smooth', block: 'start' });
  }, []);

  // #564 — the printable morning board overlay.
  const [boardOpen, setBoardOpen] = useState(false);
  const fetchesEnabled = Boolean(token) && !isValidatingSession;

  // Page-owned fetches (one source for hero + list — Principle 1).
  const dataStatus = useDashboardResource(
    () => fetchDashboardDataStatus(apiUrl, token ?? '', date),
    refreshKey,
    fetchesEnabled,
  );
  const today = useDashboardResource(
    () => fetchDashboardToday(apiUrl, token ?? '', date),
    refreshKey,
    fetchesEnabled,
  );
  // #719 — today's first-time dogs. Every role reaches this endpoint
  // (DASHBOARD_READ); the OWNER's identity is narrowed server-side for floor
  // roles, so there is no mount gate to add here.
  const assessments = useDashboardResource(
    () => fetchAssessmentsToday(apiUrl, token ?? '', date),
    refreshKey,
    fetchesEnabled,
  );
  // Wave 5 (#919) — the per-service rollup Ted reads several times a day.
  // Every role reaches it (DASHBOARD_READ); no money on it, so no gate.
  const rollup = useDashboardResource(
    () => fetchDashboardRollup(apiUrl, token ?? '', date),
    refreshKey,
    fetchesEnabled,
  );

  // Principle 5 — a single derivation decides whether today's schedule
  // zeros can be trusted; hero tiles and the list share it.
  const scheduleSuspect =
    dataStatus.data !== null && isScheduleDataSuspect(dataStatus.data);

  if (!token) {
    return (
      <section className="ferro-admin-page">
        <h1 className="ferro-section__title">Dashboard</h1>
        <p className="ferro-muted">Please sign in to continue.</p>
      </section>
    );
  }

  return (
    <section className="ferro-admin-page">
      <header className="ferro-dashboard-header">
        <div>
          <h1 className="ferro-dashboard-header__title">
            {t('admin.dashboard.todayAt', { brand: config.brand.name })}
          </h1>
          <p className="ferro-dashboard-header__subtitle">
            {new Date(`${date}T12:00:00`).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>
        {/* Quick Book CTA lives in the shell topbar (spec C3) — one tap
            from every page, not just the dashboard. */}
        {/* #564 — print the morning board (dogs expected + going home).
            Disabled until today's data has loaded, so it never prints blank. */}
        <div className="ferro-dashboard-header__actions">
        <button
          type="button"
          className="ferro-dashboard-print-btn"
          onClick={() => setBoardOpen(true)}
          disabled={!today.data}
          data-testid="dashboard-print-board"
        >
          <Icon name="print" size={20} />
          <span>Print morning board</span>
        </button>
          {/* Sawyer 02 kennel-stopgap (A1-stopgap) — same visual as the print
              button so the two header actions read as a pair; no disabled
              state (the target page loads its own data). */}
          {canOpenMedsFeeding(effectiveRolesOf(staff)) ? (
            <Link
              to={medsFeedingHref(date, 'dashboard')}
              className="ferro-dashboard-print-btn ferro-dashboard-meds-btn"
              data-testid="dashboard-meds-feeding"
            >
              <Icon name="kennel" size={20} />
              <span>{MEDS_FEEDING_LABEL}</span>
            </Link>
          ) : null}
        </div>
      </header>

      {isValidatingSession ? (
        <div
          className="ferro-dashboard__validating"
          role="status"
          aria-live="polite"
        >
          <div className="ferro-loading-screen__spinner" aria-hidden="true" />
          <p className="ferro-loading-screen__text">Loading your dashboard…</p>
        </div>
      ) : (
        <>
          {/* D2 — ONE amber page-level banner, server copy VERBATIM.
              Never swallow a dataStatus warning (Principle 5). */}
          {dataStatus.data?.warning ? (
            <div
              className="ferro-data-banner"
              role="status"
              data-testid="dashboard-data-banner"
            >
              <p>
                <span className="ferro-data-banner__title">
                  {t('admin.dashboard.banner.title')}
                </span>
                {dataStatus.data.warning}
              </p>
            </div>
          ) : null}

          {/* #431 — first login only: the manager/receptionist blurb from
              their invite email, echoed on the desk home the email pointed
              at. Deliberately BELOW the dataStatus banner: an orientation
              card must never push an honesty warning off the first glance
              (Principle 5 outranks Principle 2 when they compete). Owners
              get no card — same rule as the invite email. */}
          <WelcomeCard config={config} />
          {/* Sawyer 15 workweek-hours-ot (E2) — near-overtime watch for owner/manager; the gate controls the mount (no request for other roles). */}
          <DashboardOvertimeWatch apiUrl={apiUrl} roles={effectiveRolesOf(staff)} fetchesEnabled={fetchesEnabled} refreshKey={refreshKey} />

          {/* Wave 5 (#922, Ted #18) — the hero's first band is the live
              snapshot ON SITE NOW · ARRIVING · GOING HOME · OVERNIGHT
              TONIGHT, read from the SAME /rollup the DailyRollup table and
              the day list read (Principle 1), every number a tap into
              /calendar/day. The #563 StatusCounts strip that used to sit
              here counted BOOKINGS above a hero counting dogs; the hero
              absorbs it (its breakdown is the rollup's rows, its
              needs-attention count is the alert strip). */}
          <FacilityHero
            apiUrl={apiUrl}
            token={token}
            date={date}
            refreshKey={refreshKey}
            dataStatus={dataStatus.data}
            scheduleSuspect={scheduleSuspect}
            rollup={rollup.data}
            rollupLoading={rollup.isLoading}
            rollupError={rollup.error}
            onRetryRollup={rollup.retry}
            onCheckInJump={jumpToArrivals}
          />

          {/* #719 — the one-to-three first-time dogs, ABOVE the full
              arrivals list: Kylie's ask was to see them "right away when I
              walk in", and a panel she has to scroll past the whole day's
              board to reach does not answer it. Small by construction (a
              cap-3 service), and shares the page's dataStatus derivation so
              its empty state can't contradict the list below (Principle 1).
              Assessments are dark for every tenant today, so what renders
              here in production is the "not set up yet" state — see
              AssessmentsToday.tsx's empty-state matrix. */}
          <AssessmentsToday
            data={assessments.data}
            isLoading={assessments.isLoading}
            error={assessments.error}
            onRetry={assessments.retry}
            dataSuspect={scheduleSuspect}
          />

          {/* Sawyer, 10-30: "There should be a check in box next to the arriving
              dogs on the dashboard." The buttons were there, under the rollup,
              about two screens down. The one list the desk acts on now comes
              straight after the numbers that describe it (where front desk and
              owner needs conflict on a shared screen, front desk wins), and the
              hero's "Check in arriving dogs" jumps here from the first screen. */}
          <div ref={arrivalsRef} className="ferro-dashboard__arrivals">
            <ArrivalsDepartures
              apiUrl={apiUrl}
              token={token}
              data={today.data}
              isLoading={today.isLoading}
              error={today.error}
              onRetry={today.retry}
              dataSuspect={scheduleSuspect}
              onMutation={bumpRefresh}
              runCardPrompt={config.features?.runCardPromptOnCheckIn !== false}
            />
          </div>

          {/* Wave 5 (#919) — Ted's daily rollup: one row per service,
              Expected · Check-ins · Check-outs · Overnight · Total in DOGS,
              every number a link into the day list pre-filtered to exactly
              the dogs it counted (the server folds the counts from the same
              rows the list renders — Principle 1). Below the arrivals list
              since Sawyer's 10-30 ask. */}
          <DailyRollup
            rollup={rollup.data}
            isLoading={rollup.isLoading}
            error={rollup.error}
            onRetry={rollup.retry}
            dataSuspect={scheduleSuspect}
            date={date}
          />

          {/* D3 — money/insight widgets mount only for roles the matrix
              allows (owner/manager). Receptionist: zero fetches here. */}
          {canSeeRevenue ? (
            <>
              <div className="ferro-dashboard-grid">
                <RevenueChart config={config} />
                <SegmentCards config={config} />
              </div>

              <InsightsPanel config={config} />
            </>
          ) : null}

          {/* #564 — printable morning board (portals to <body>). Same
              /today source as the list above (Principle 1). */}
          {boardOpen ? (
            <ArrivalsBoard
              facilityName={config.brand.name}
              date={date}
              arrivals={today.data?.arrivals ?? []}
              departures={today.data?.departures ?? []}
              // Sawyer 03 today-notes-board (A4) — mid-stay boarders (absent on an older server).
              inHouse={today.data?.onSite}
              dataSuspect={scheduleSuspect}
              warning={dataStatus.data?.warning ?? null}
              onClose={() => setBoardOpen(false)}
            />
          ) : null}
        </>
      )}
    </section>
  );
}
